<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, defineAsyncComponent } from '#imports';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from '#imports';
import { useAuthState } from '~/composables/useAuthState';
import { useLocale } from '~/composables/useLocale';

// 延迟导入非关键组件
const PointsHistoryPopup = defineAsyncComponent(() =>
  import('~/components/PointsHistoryPopup.vue')
);

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

// 使用统一的认证状态管理
const {
  user,
  isAuthenticated,
  isLoggingIn,
  handleSignIn,
  totalPoints,
  isLoadingAccount,
  handleLogout,
} = useAuthState();

const { getCurrentLocale, setLocale, availableLocales } = useLocale();

// 积分历史弹窗
const showPointsHistoryPopup = ref(false);

// 侧边栏菜单相关
const showSidebar = ref(false);
const showLanguageModal = ref(false); // 语言切换弹窗
const showUserMenu = ref(false); // 用户头像菜单
const currentLocale = computed(() => getCurrentLocale());

// 切换语言
const changeLocale = (code: string) => {
  if (setLocale(code)) {
    showLanguageModal.value = false;
    showSidebar.value = false;
  }
};

// 打开语言切换弹窗
const openLanguageModal = () => {
  showLanguageModal.value = true;
};

// 关闭语言切换弹窗
const closeLanguageModal = () => {
  showLanguageModal.value = false;
};

// 处理本地登出UI
const handleLocalLogout = async () => {
  await handleLogout();
  showSidebar.value = false;
  showUserMenu.value = false;
};

// 打开用户菜单
const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value;
};

// 打开积分明细并关闭菜单
const openPointsHistory = () => {
  showPointsHistoryPopup.value = true;
  showUserMenu.value = false;
};

// 获取用户显示名称
const displayName = computed(() => {
  if (user.value) {
    return user.value.firstName || user.value.username || 'User';
  }
  return 'User';
});

// 获取用户头像
const userAvatar = computed(() => {
  if (user.value && user.value.imageUrl) {
    return user.value.imageUrl;
  }
  return null; // 返回null将使用默认头像
});

// 滚动状态
const isScrolled = ref(false);
const sidebarRef = ref<HTMLElement | null>(null);
const languageModalRef = ref<HTMLElement | null>(null);
const userMenuRef = ref<HTMLElement | null>(null);

// 优化挂载处理
onMounted(() => {
  // 延迟注册不立即需要的事件监听
  setTimeout(() => {
    if (typeof window !== 'undefined') {
      // 监听滚动事件
      window.addEventListener('scroll', handleScroll);
      // 监听点击外部关闭用户菜单
      document.addEventListener('click', handleClickOutside);
      // 初始检查滚动状态
      handleScroll();
    }
  }, 500); // 延迟500ms注册
});

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('scroll', handleScroll);
    document.removeEventListener('click', handleClickOutside);
  }
});

// 点击外部关闭用户菜单
const handleClickOutside = (event: MouseEvent) => {
  const userMenu = userMenuRef.value;
  if (userMenu instanceof HTMLElement && event.target instanceof Node && !userMenu.contains(event.target)) {
    showUserMenu.value = false;
  }
};

// 处理滚动事件
const handleScroll = () => {
  if (typeof window !== 'undefined') {
    isScrolled.value = window.scrollY > 10;
  }
};

// 打开积分历史弹窗
const openPointsHistoryPopup = () => {
  showPointsHistoryPopup.value = true;
  showSidebar.value = false; // 关闭侧边栏
};

// 语言代码到国旗表情符号的映射
const getLanguageFlag = (code: string) => {
  const flagMap: Record<string, string> = {
    'en': '🇬🇧',
    'zh': '🇨🇳',
    'zh-TW': '🇭🇰',
    'ja': '🇯🇵',
    'ko': '🇰🇷',
    'fr': '🇫🇷',
    'de': '🇩🇪',
    'es': '🇪🇸'
  };
  return flagMap[code] || '🌐';
};

const navItems = [
  { icon: "i-lucide-home", key: "nav.home", route: "/" },
  {
    icon: "i-lucide-edit",
    key: "nav.create",
    route: "/create",
    children: [
      { icon: "i-lucide-music", key: "nav.createMusic", route: "/create" },
      { icon: "i-lucide-clapperboard", key: "nav.clips", route: "/clips" },
      { icon: "i-lucide-video", key: "nav.mvGenerator", route: "/mv-generator" },
      { icon: "i-lucide-mic", key: "nav.rapLyric", route: "/rap-lyrics-generator" },
      { icon: "i-lucide-file-text", key: "nav.lyricGenerator", route: "/lyrics-generator" },
      { icon: "i-lucide-image", key: "nav.albumCover", route: "/album-cover-generator" },
      // MV 生成功能暂不支持，隐藏入口
      // { icon: "i-lucide-video", key: "nav.mvGenerator", route: "/mv-generator" }
    ]
  },
  {
    icon: "i-lucide-wrench",
    key: "nav.tools",
    route: "/rap-name-generator",
    children: [
      { icon: "i-lucide-user", key: "nav.rapnameGenerator", route: "/rap-name-generator" },
      { icon: "i-lucide-scissors", key: "nav.vocalSeparation", route: "/vocal-separation" },
    ]
  },
  { icon: "i-lucide-library", key: "nav.library", route: "/library" },
  { icon: "i-lucide-crown", key: "nav.pricing", route: "/pricing" },
] as const;
type NavItem = (typeof navItems)[number];
type NavItemWithChildren = Extract<NavItem, { children: readonly unknown[] }>;

const hasChildren = (item: NavItem): item is NavItemWithChildren => 'children' in item;

const isNavRouteActive = (targetRoute: string) => {
  if (targetRoute === '/clips') {
    return route.path === '/clips' || route.path.startsWith('/clips/');
  }
  return route.path === targetRoute;
};

// 菜单展开状态（支持多个可展开菜单）
const expandedMenus = ref<Record<string, boolean>>({ 'nav.create': true, 'nav.tools': false });

const handleNavClick = (item: NavItem) => {
  if (hasChildren(item)) {
    expandedMenus.value[item.key] = !expandedMenus.value[item.key];
    return;
  }
  router.push(item.route);
  showSidebar.value = false;
}

const handleSubNavClick = (route: string) => {
  router.push(route);
  showSidebar.value = false;
}

</script>

<template>
  <!-- 移动端顶部导航 -->
  <div class="lg:hidden flex items-center justify-between gap-2 overflow-hidden px-3 py-4 sm:px-4 sticky top-0 z-50 transition-all duration-300" :class="[
    isScrolled
      ? 'bg-gray-900/60 backdrop-blur-lg shadow-md'
      : 'bg-transparent'
  ]">
    <div class="flex min-w-0 flex-1 items-center gap-2">
      <!-- 汉堡菜单按钮 -->
      <button
        @click="showSidebar = true"
        class="shrink-0 text-2xl p-1"
        :class="isScrolled ? 'text-white' : 'text-gray-800 dark:text-gray-200'"
      >
        <div class="i-lucide-menu"></div>
      </button>

      <div class="flex min-w-0 items-center" @click="router.push('/')">
        <!-- 使用优化的图片组件 -->
        <OptimizedImage
          src="/images/logo.png"
          alt="MusicMint AI Music Generator Logo"
          width="24"
          height="24"
          :isLazy="false"
          :isAboveTheFold="true"
          fetchPriority="high"
          imgClass="w-6 h-6"
        />
        <div class="ml-2 max-w-[128px] truncate text-lg font-semibold" :class="isScrolled ? 'text-white' : 'text-gray-800 dark:text-gray-200'">
          MusicMint
        </div>
      </div>
    </div>

    <div class="flex shrink-0 items-center gap-1.5 sm:gap-3">
      <!-- 首页按钮 - 仅在非首页显示 -->
      <button
        v-if="route.path !== '/'"
        @click.stop="router.push('/')"
        class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100/20 transition-colors sm:h-9 sm:w-9"
        :class="isScrolled ? 'text-white' : 'text-gray-800 dark:text-gray-200'"
        :aria-label="t('nav.home')"
      >
        <div class="i-lucide-home text-xl"></div>
      </button>

      <!-- 音乐库按钮 -->
      <button
        @click.stop="router.push('/library')"
        class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100/20 transition-colors sm:h-9 sm:w-9"
        :class="isScrolled ? 'text-white' : 'text-gray-800 dark:text-gray-200'"
        :aria-label="t('nav.library')"
      >
        <div class="i-lucide-library text-xl"></div>
      </button>

      <ClientOnly>
      <!-- 未登录状态或没有 token -->
      <div v-if="!isAuthenticated">
        <button @click="handleSignIn" class="mobile-login-button" :disabled="isLoggingIn">
          <span v-if="isLoggingIn" class="mobile-loading-spinner"></span>
          {{ t('user.login') }}
        </button>
      </div>

      <!-- 已登录状态：用户信息 -->
      <div v-else class="relative" ref="userMenuRef">
        <button @click.stop="toggleUserMenu"
          class="flex items-center rounded-full p-1 hover:bg-gray-100/20 transition-colors">
          <!-- 用户头像 -->
          <div v-if="userAvatar" class="w-8 h-8 rounded-full overflow-hidden">
            <OptimizedImage
              :src="userAvatar"
              alt="User Avatar"
              width="32"
              height="32"
              imgClass="w-full h-full object-cover"
              :isLazy="false"
            />
          </div>
          <div v-else class="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white">
            <div class="i-lucide-user text-lg"></div>
          </div>
        </button>

        <!-- 用户下拉菜单 -->
        <div v-if="showUserMenu"
          class="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-50"
          @click.stop>
          <!-- 积分 -->
          <button @click="openPointsHistory"
            class="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <div class="i-lucide-coins text-xl text-yellow-500"></div>
            <div class="flex-1 text-left">
              <div class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ t('subscription.points') }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400">{{ totalPoints }}</div>
            </div>
          </button>
          <NuxtLink to="/billing" @click="showUserMenu = false"
            class="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-gray-100">
            <div class="i-lucide-credit-card text-xl" aria-hidden="true"></div>
            <span class="text-sm font-medium">{{ t('billing.membershipEntry') }}</span>
          </NuxtLink>
          <!-- 退出登录 -->
          <button @click="handleLocalLogout"
            class="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-colors border-t border-gray-200 dark:border-gray-700">
            <div class="i-lucide-log-out text-xl"></div>
            <span class="text-sm font-medium">{{ t('user.logout') }}</span>
          </button>
        </div>
      </div>
      </ClientOnly>
    </div>
  </div>

  <!-- 侧边栏遮罩 -->
  <div
    v-if="showSidebar"
    class="fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300"
    @click="showSidebar = false"
  ></div>

  <!-- 侧边栏 -->
  <div
    class="fixed top-0 left-0 h-full w-64 bg-white dark:bg-gray-900 z-[70] transform transition-transform duration-300 shadow-xl overflow-y-auto"
    :class="showSidebar ? 'translate-x-0' : '-translate-x-full'"
    ref="sidebarRef"
  >
    <div class="p-6 flex flex-col h-full">
      <!-- 侧边栏头部 -->
      <div class="flex items-center justify-between mb-8">
        <div class="flex items-center gap-3">
          <img src="/images/logo.png" alt="Logo" class="w-8 h-8" />
          <span class="text-xl font-bold dark:text-white">MusicMint</span>
        </div>
        <button @click="showSidebar = false" class="text-gray-500 dark:text-gray-400">
          <div class="i-lucide-x text-2xl"></div>
        </button>
      </div>

      <!-- 导航链接 -->
      <div class="flex-1 overflow-y-auto space-y-2 mb-6">
        <div v-for="item in navItems" :key="item.key">
          <!-- 主菜单项 -->
          <a
            :href="item.route"
            @click.prevent="handleNavClick(item)"
            class="flex items-center justify-between px-4 py-3 rounded-xl transition-colors cursor-pointer"
            :class="(isNavRouteActive(item.route) && !hasChildren(item)) || (hasChildren(item) && expandedMenus[item.key])
              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'"
          >
            <div class="flex items-center gap-4">
              <div :class="[item.icon, 'text-xl']"></div>
              <span class="font-medium">{{ t(item.key) }}</span>
            </div>
            <!-- 展开箭头 -->
            <div v-if="item.children"
              class="i-lucide-chevron-down text-lg transition-transform duration-200"
              :class="{ 'rotate-180': expandedMenus[item.key] }"
            ></div>
          </a>

          <!-- 子菜单项 (仅针对有 children 的项) -->
          <div v-if="item.children" class="overflow-hidden transition-all duration-300"
            :class="expandedMenus[item.key] ? 'max-h-[500px] opacity-100 mt-1' : 'max-h-0 opacity-0'">
            <a
              v-for="child in item.children"
              :key="child.key"
              :href="child.route"
              @click.prevent="handleSubNavClick(child.route)"
              class="flex items-center gap-4 px-4 py-2.5 rounded-xl transition-colors cursor-pointer ml-4"
              :class="isNavRouteActive(child.route)
                ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/10'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'"
            >
              <div :class="[child.icon, 'text-lg']"></div>
              <span class="text-sm">{{ t(child.key) }}</span>
            </a>
          </div>
        </div>
      </div>

      <!-- 底部操作区 -->
      <div class="border-t border-gray-200 dark:border-gray-800 pt-6">
        <!-- 语言设置按钮 -->
        <button
          @click="openLanguageModal"
          class="w-full flex items-center justify-between px-4 py-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors mb-2"
        >
          <div class="flex items-center gap-3 text-gray-700 dark:text-gray-300">
            <div class="i-lucide-languages text-xl"></div>
            <span>{{ t('settings.language') }}</span>
          </div>
          <div class="flex items-center gap-2 text-sm text-gray-500">
            <span>{{ getLanguageFlag(currentLocale) }}</span>
            <div class="i-lucide-chevron-right"></div>
          </div>
        </button>

        <!-- 登录按钮（未登录时显示） -->
        <button
          v-if="!isAuthenticated"
          @click="handleSignIn"
          :disabled="isLoggingIn"
          class="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white transition-colors font-medium"
        >
          <span v-if="isLoggingIn" class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
          <div v-else class="i-lucide-log-in text-xl"></div>
          <span>{{ t('user.login') }}</span>
        </button>

        <!-- 退出登录（已登录时显示） -->
        <button
          v-else
          @click="handleLocalLogout"
          class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          <div class="i-lucide-log-out text-xl"></div>
          <span>{{ t('user.logout') }}</span>
        </button>
      </div>
    </div>
  </div>

  <!-- 语言切换弹窗 -->
  <Teleport to="body">
    <div v-if="showLanguageModal" class="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4">
      <div class="bg-white dark:bg-gray-800 rounded-2xl max-w-sm w-full p-6 shadow-2xl" ref="languageModalRef">
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100">{{ t('common.selectLanguage') }}</h3>
          <button @click="closeLanguageModal"
            class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200 transition-colors">
            <div class="i-lucide-x text-xl"></div>
          </button>
        </div>
        <div class="grid grid-cols-1 gap-2 max-h-[60vh] overflow-y-auto">
          <button v-for="locale in availableLocales" :key="locale.code"
            @click="changeLocale(locale.code)"
            class="flex items-center justify-between p-4 rounded-xl transition-all border border-transparent"
            :class="currentLocale === locale.code
              ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
              : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-gray-200 dark:hover:border-gray-600'">
            <div class="flex items-center">
              <span class="text-2xl mr-4">{{ getLanguageFlag(locale.code) }}</span>
              <span class="font-medium text-gray-900 dark:text-gray-100">{{ locale.name }}</span>
            </div>
            <div v-if="currentLocale === locale.code" class="text-blue-600 dark:text-blue-400">
              <div class="i-lucide-check-circle text-xl"></div>
            </div>
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- 积分历史弹窗 -->
  <PointsHistoryPopup v-model:visible="showPointsHistoryPopup" />
</template>

<style scoped>
/* 移除导航链接的默认样式 */
a {
  text-decoration: none;
  color: inherit;
}

/* 确保导航栏在滚动时平滑过渡 */
.sticky {
  position: fixed;
  width: 100%;
  left: 0;
  top: 0;
}

/* 增加导航栏的高度和美观度 */
.lg\:hidden {
  height: 64px;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

/* 增强磨砂玻璃效果 */
.backdrop-blur-lg {
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

/* 移动端登录按钮样式 */
.mobile-login-button {
  @apply px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-full text-sm font-medium transition-colors shadow-lg shadow-blue-500/30;
  max-width: 82px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 加载动画 */
.mobile-loading-spinner {
  @apply inline-block w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin;
}

/* 隐藏滚动条 */
.overflow-y-auto::-webkit-scrollbar {
  display: none;
}
.overflow-y-auto {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
